import type { BusinessCdd, UpdateBusinessCddInput } from 'hooks/internalApi';
import { useToast } from 'components/ToastManager';
import {
  useAuthorizePlatformEntityBasisTheorySessionMutation,
  useGetPaymentPlatformEntityQuery,
  useUpdateBusinessCddMutation,
} from 'hooks/internalApi';
import { useContextSafely } from 'hooks/useContextSafely';
import { useInternalApiContext } from 'hooks/useInternalApiContext';
import { useWorkspaceId } from 'hooks/useWorkspace';
import { isNil, omitBy, pick } from 'lodash-es';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { match } from 'ts-pattern';
import { FragmentEnv } from 'utils/env';
import { getResultOrError } from 'utils/errors';
import { makePath } from 'utils/routes';
import { useLocation } from 'wouter';
import type { ITextElement } from '@basis-theory/react-elements';
import {
  BasisTheoryProvider,
  useBasisTheory,
} from '@basis-theory/react-elements';
import { useQuery } from '@tanstack/react-query';

import { ColorModeContext } from '@fragment/ui/src/components/ColorMode/ColorModeContext';
import { Toast } from '@fragment/ui/src/components/Toast';

import type { SideBarStageName } from './OnboardingLayout';
import type { OnboardingState } from './onboardingReducer';
import { AccountsView } from './AccountsView';
import { BusinessCDDView } from './BusinessCDDView';
import { BusinessView } from './BusinessView';
import { IncorporationView } from './IncorporationView';
import { NoticeView } from './NoticeView';
import { OnboardingLayout } from './OnboardingLayout';
import { onboardingReducer } from './onboardingReducer';
import { PeopleView } from './PeopleView';
import { PlatformOnboardingSubmitView } from './PlatformOnboardingSubmitView';
import { FormLoading } from './SharedUI';

const STAGE_ORDER: SideBarStageName[] = [
  'notice',
  'business',
  'incorporation',
  'businessCDD',
  'people',
  'accounts',
  'submit',
];

const getNextStage = (stage: SideBarStageName): SideBarStageName => {
  const idx = STAGE_ORDER.indexOf(stage);
  if (idx === STAGE_ORDER.length - 1) {
    return stage;
  }
  return STAGE_ORDER[idx + 1];
};

const STAGES_THAT_UPDATE_BUSINESS_CDD: SideBarStageName[] = [
  'notice',
  'incorporation',
  'businessCDD',
  'people',
];

// fields in the CDD doc that are not in the update call
const fieldsToInclude: (keyof BusinessCdd)[] = [
  'allowsCrossBorder',
  'buyerSellerConnection',
  'changes',
  'countriesOfOperation',
  'description',
  'hasUndergoneSignificantChanges',
  'holdFinancialLicenses',
  'industryVertical',
  'isRegulatedIndustry',
  'licenses',
  'sellersAsIntermediaries',
  'strategicPartners',
  'structure',
  'termsAgreedAt',
];

const cleanBusinessCdd = (
  businessCdd: BusinessCdd | undefined
): OnboardingState => {
  // remove __typename from licenses
  const cddLicenseState = businessCdd?.licenses
    ?.filter(
      (license): license is NonNullable<typeof license> => license != null
    )
    .map((license) => ({
      type: license.type,
      number: license.number,
    }));
  return {
    ...omitBy(pick(businessCdd ?? {}, fieldsToInclude), isNil),
    ...(cddLicenseState != null ? { licenses: cddLicenseState } : {}),
  };
};

const onboardingStateToUpdateInput = (
  businessCdd: BusinessCdd | undefined,
  state: OnboardingState
): UpdateBusinessCddInput => {
  // remove undesired fields from doc and null values
  const strippedCDD = cleanBusinessCdd(businessCdd);
  const strippedState = omitBy(state, isNil);

  return {
    ...strippedCDD,
    ...strippedState,
  };
};

const mergeCDDAndState = (
  businessCdd: BusinessCdd | undefined,
  state: OnboardingState
): OnboardingState => {
  const strippedCDD = cleanBusinessCdd(businessCdd);
  return {
    ...strippedCDD,
    ...state,
  };
};

export const PlatformOnboardingView = () => {
  const workspaceId = useWorkspaceId();
  const [, setLocation] = useLocation();
  const { mode, systemIsDark } = useContextSafely(ColorModeContext);
  const isDarkMode = mode === 'dark' || (mode === 'system' && systemIsDark);
  // get entity
  const { context: internalApiCtx } = useInternalApiContext();
  const [{ data: entityData, error: entityError, fetching: isFetchingEntity }] =
    useGetPaymentPlatformEntityQuery({
      variables: { workspaceId },
      context: internalApiCtx,
    });

  const { result, isError: isEntityError } = getResultOrError(
    entityData?.paymentPlatformEntity,
    entityError
  );

  const piiDataTokenId = useMemo(
    () => result?.platformEntity.piiDataTokenId,
    [result]
  );

  // authorize basis theory session
  const { bt } = useBasisTheory();
  const [, authorizeSession] =
    useAuthorizePlatformEntityBasisTheorySessionMutation();
  const [, updateBusinessCdd] = useUpdateBusinessCddMutation();

  const { showToast } = useToast();
  const {
    data: sessionKey,
    error: sessionError,
    isFetching: isFetchingSession,
  } = useQuery({
    queryKey: ['bt-session', workspaceId],
    enabled: bt != null,
    retry: 3,
    queryFn: async () => {
      const { sessionKey: key, nonce } = await bt!.sessions.create();

      const authResult = await authorizeSession(
        { workspaceId, nonce },
        internalApiCtx
      );

      if (authResult.error) {
        throw new Error(authResult.error.message);
      }

      const data = authResult.data?.authorizePlatformEntityBasisTheorySession;
      if (!data) {
        throw new Error('No response from server');
      }

      if (
        data.__typename === 'BadRequestError' ||
        data.__typename === 'InternalError'
      ) {
        throw new Error(data.message);
      }

      if (!data.success) {
        throw new Error('Session authorization failed');
      }

      return key;
    },
  });

  // fill in data from retrieved token
  const {
    data: token,
    error: tokenError,
    refetch: retrieveToken,
    isFetching: isFetchingToken,
  } = useQuery({
    queryKey: ['token', piiDataTokenId],
    enabled: bt != null && sessionKey != null && piiDataTokenId != null,
    retry: 3,
    queryFn: async () =>
      bt!.tokens.retrieve(piiDataTokenId!, {
        apiKey: sessionKey!,
      }),
  });

  const updateToken = useCallback(
    (id: string, ref: React.RefObject<ITextElement>) => {
      if (bt && piiDataTokenId && ref.current) {
        bt.tokens
          .update(piiDataTokenId, {
            data: {
              [id]: ref.current,
            },
          })
          .then((_res) => {})
          .catch(() => {
            showToast(<Toast type="error" message="Failed to save data." />);
          });
      }
    },
    [bt, piiDataTokenId, showToast]
  );

  const [currentStage, setCurrentStage] = useState<SideBarStageName>('notice');
  const [completedStages, setCompletedStages] = useState<SideBarStageName[]>(
    []
  );

  // side effect to re-fetch token when current stage changes
  useEffect(() => {
    retrieveToken();
  }, [currentStage, retrieveToken]);

  const [onboardingState, dispatch] = useReducer(onboardingReducer, {});

  const setOnboardingField = useCallback(
    (
      stateKey: string,
      value: string | string[] | boolean | { type: string; number: string }[]
    ) => {
      dispatch({ type: 'SET_FIELD', stateKey, value });
    },
    []
  );

  const handleNextClick = useCallback(async () => {
    const shouldUpdateBusinessCdd =
      STAGES_THAT_UPDATE_BUSINESS_CDD.includes(currentStage);

    if (shouldUpdateBusinessCdd && result?.businessCdd.instance != null) {
      const updateResult = await updateBusinessCdd(
        {
          workspaceId,
          instance: result.businessCdd.instance,
          businessCdd: onboardingStateToUpdateInput(
            result.businessCdd,
            onboardingState
          ),
        },
        internalApiCtx
      );

      if (updateResult.error) {
        showToast(<Toast type="error" message={updateResult.error.message} />);
        return;
      }

      const data = updateResult.data?.updateBusinessCDD;
      if (
        data?.__typename === 'BadRequestError' ||
        data?.__typename === 'InternalError'
      ) {
        showToast(<Toast type="error" message={data.message} />);
        return;
      }
    }

    setCompletedStages((prev) => [...prev, currentStage]);
    setCurrentStage(getNextStage(currentStage));
  }, [
    currentStage,
    result,
    onboardingState,
    workspaceId,
    showToast,
    internalApiCtx,
    updateBusinessCdd,
  ]);

  if (isFetchingEntity || isFetchingSession || isFetchingToken || !token) {
    return (
      <OnboardingLayout
        currentStage={currentStage}
        completedStages={completedStages}
        onClickStage={setCurrentStage}
        workspaceId={workspaceId}
      >
        <FormLoading />
      </OnboardingLayout>
    );
  }
  if (sessionError || tokenError || isEntityError) {
    return (
      <OnboardingLayout
        currentStage={currentStage}
        completedStages={completedStages}
        onClickStage={setCurrentStage}
        workspaceId={workspaceId}
      >
        <div>Failed to load onboarding data, please refresh the page.</div>
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout
      currentStage={currentStage}
      completedStages={completedStages}
      onClickStage={setCurrentStage}
      workspaceId={workspaceId}
    >
      {match(currentStage)
        .with('notice', () => (
          <NoticeView
            onboardingState={mergeCDDAndState(
              result.businessCdd,
              onboardingState
            )}
            setOnboardingField={setOnboardingField}
            onNext={handleNextClick}
          />
        ))
        .with('business', () => (
          <BusinessView
            token={token}
            updateToken={updateToken}
            isDarkMode={isDarkMode}
            onNext={handleNextClick}
          />
        ))
        .with('incorporation', () => (
          <IncorporationView
            onboardingState={mergeCDDAndState(
              result.businessCdd,
              onboardingState
            )}
            setOnboardingField={setOnboardingField}
            token={token}
            updateToken={updateToken}
            isDarkMode={isDarkMode}
            onNext={handleNextClick}
          />
        ))
        .with('businessCDD', () => (
          <BusinessCDDView
            onboardingState={mergeCDDAndState(
              result.businessCdd,
              onboardingState
            )}
            setOnboardingField={setOnboardingField}
            onNext={handleNextClick}
          />
        ))
        .with('people', () => <PeopleView onNext={handleNextClick} />)
        .with('accounts', () => (
          <AccountsView goToPlaid={() => {}} goToNextPage={handleNextClick} />
        ))
        .with('submit', () => (
          <PlatformOnboardingSubmitView
            workspaceId={workspaceId}
            legalEntityId={result.platformEntity.id}
            onSubmit={() => {
              setLocation(
                makePath(['w', workspaceId, 'payments', 'payment-settings'], {
                  absolute: true,
                })
              );
            }}
          />
        ))
        .exhaustive()}
    </OnboardingLayout>
  );
};

export const PlatformOnboarding = () => {
  const { dashboardApiKey } = FragmentEnv.basisTheory!;
  const { bt } = useBasisTheory(dashboardApiKey);
  if (!dashboardApiKey) {
    return <div>Basis Theory is not configured for this environment.</div>;
  }
  if (!bt) {
    return (
      <OnboardingLayout
        currentStage="notice"
        completedStages={[]}
        onClickStage={() => {}}
        workspaceId=""
      >
        <FormLoading />
      </OnboardingLayout>
    );
  }
  return (
    <BasisTheoryProvider bt={bt}>
      <Suspense
        fallback={
          <OnboardingLayout
            currentStage="notice"
            completedStages={[]}
            onClickStage={() => {}}
            workspaceId=""
          >
            <FormLoading />
          </OnboardingLayout>
        }
      >
        <PlatformOnboardingView />
      </Suspense>
    </BasisTheoryProvider>
  );
};
